/**
 * MailDraftsListPage — paginated table of the current operator's drafts.
 * Click a row to resume the draft.
 */
import { useQuery } from '@tanstack/react-query';
import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';

import { listDrafts } from '../../lib/api/mail-drafts';
import type { ListDraftsResponse } from '../../lib/api/types';

const PAGE_SIZE = 20;

export function MailDraftsListPage(): JSX.Element {
  const [page, setPage] = useState(1);

  const query = useQuery<ListDraftsResponse, Error>({
    queryKey: ['mail-drafts', { page, pageSize: PAGE_SIZE, status: 'DRAFT' }],
    queryFn: async () => listDrafts({ page, pageSize: PAGE_SIZE, status: 'DRAFT' }),
  });

  return (
    <section className="space-y-4" data-testid="mail-drafts-list">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">My drafts</h1>
          <p className="text-sm text-slate-600">
            Resume a draft you started earlier.
          </p>
        </div>
        <Link
          to="/mail/register"
          className="rounded bg-nipost-600 px-4 py-2 text-sm font-semibold text-white"
        >
          New mail
        </Link>
      </header>

      {query.isPending && <p className="text-sm text-slate-500">Loading…</p>}
      {query.isError && (
        <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {query.error.message}
        </p>
      )}

      {query.data && (
        <>
          <table className="w-full overflow-hidden rounded border border-slate-200 bg-white text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Reference</th>
                <th className="px-4 py-2">Step</th>
                <th className="px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                    No drafts yet.
                  </td>
                </tr>
              )}
              {query.data.items.map((draft) => (
                <tr key={draft.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/mail/register/${draft.id}`}
                      className="font-mono text-nipost-700"
                    >
                      {draft.draftRef}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs uppercase tracking-wide text-slate-600">
                    {draft.step}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {new Date(draft.updatedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              Page {query.data.page} · {query.data.total} drafts
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={query.data.items.length < PAGE_SIZE}
                className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
